class StayAISubscriptions extends HTMLElement {
  constructor() {
    super();
    this.productJSON = this.getAttribute('productData');
    this.product = JSON.parse(this.productJSON);
    this.initialVariant = this.getAttribute('currentVariantId');
    this.sellingPlanGroups = JSON.parse(this.getAttribute('sellingPlanGroups'));
    this.productFormId = this.getAttribute('productFormId');
    this.frequencyVisible = this.getAttribute('frequencyVisible');
  }
  connectedCallback() {
    this.initWidget();
    this.setupEventListeners();
  }
  getAllowedSellingPlanIds() {
    return new Set(
      this.sellingPlanGroups
        .flatMap(group =>
          (group?.selling_plans || []).map(plan => String(plan.id))
        )
        .filter(Boolean)
    );
  }
  isAllowedSellingPlanId(sellingPlanId) {
    if (
      sellingPlanId === null ||
      sellingPlanId === undefined ||
      sellingPlanId === ''
    )
      return true;
    return this.getAllowedSellingPlanIds().has(String(sellingPlanId));
  }
  getProductForm() {
    let productForm = document.querySelector(
      'form[action="/cart/add"][data-type="add-to-cart-form"]:not(.installment), ' +
        'form[action="/cart/add"][id*="product-form"]:not(.installment), ' +
        'form[action="/cart/add"][class*="shopify-product-form"]:not(.installment), ' +
        'form[action="/cart/add"][class*="product-form"]:not(.installment), ' +
        'form[action="/cart/add"]:not(.installment)'
    );
    if (this.productFormId !== '')
      productForm = document.querySelector(this.productFormId) || productForm;
    return productForm;
  }
  getAssociatedSellingPlanInputs(productForm) {
    if (!productForm) return [];
    const formId = productForm.id;
    const inForm = Array.from(
      productForm.querySelectorAll("input[name='selling_plan']")
    );
    if (!formId) return inForm;
    const associated = Array.from(
      document.querySelectorAll(`input[name='selling_plan'][form="${formId}"]`)
    );
    return Array.from(new Set([...inForm, ...associated]));
  }
  ensureManagedSellingPlanInput(productForm) {
    if (!productForm) return null;
    let sellingPlanInput = this.getAssociatedSellingPlanInputs(
      productForm
    ).find(input => input.hasAttribute('data-stayai-managed-selling-plan'));
    if (!sellingPlanInput) {
      sellingPlanInput = productForm.querySelector(
        "input[name='selling_plan']"
      );
    }
    if (!sellingPlanInput) {
      sellingPlanInput = document.createElement('input');
      sellingPlanInput.type = 'hidden';
      sellingPlanInput.name = 'selling_plan';
      productForm.appendChild(sellingPlanInput);
    }
    sellingPlanInput.setAttribute('data-stayai-managed-selling-plan', 'true');
    return sellingPlanInput;
  }
  enforceSellingPlanGuard() {
    const productForm = this.getProductForm();
    if (!productForm) return;
    const managedInput = this.ensureManagedSellingPlanInput(productForm);
    const inputs = this.getAssociatedSellingPlanInputs(productForm);
    inputs.forEach(input => {
      if (input === managedInput) {
        input.disabled = false;
        if (!this.isAllowedSellingPlanId(input.value)) {
          input.value = '';
        }
        return;
      }
      // Prevent any non-StayAI selling_plan values from being submitted.
      input.disabled = true;
    });
  }
  initWidget() {
    this.currentQuantity = 1;
    const productForm = this.getProductForm();
    const qtyInput = productForm?.querySelector(
      'input[name="quantity"], select[name="quantity"]'
    );
    if (qtyInput?.value) {
      const parsed = parseInt(qtyInput.value, 10);
      if (!Number.isNaN(parsed) && parsed >= 1) this.currentQuantity = parsed;
    }
    this.purchaseTypeSubscription = this.querySelector(
      "[for='purchaseTypeSubscription']"
    );
    this.purchaseTypeOneTime = this.querySelector(
      "[for='purchaseTypeOneTime']"
    );
    this.sellingPlanSelector = this.querySelector(
      '.stayai-subscription-dropdown'
    );
    this.subscriptionBox = this.querySelector(
      '[data-retextion-subscription-box]'
    );
    this.subscriptionBoxContainer = this.querySelector(
      '.stayai-subscription-frequency-container'
    );
    this.radioOneTime = this.querySelector(
      'input[value="purchaseTypeOneTime"]'
    );
    this.radioSubscription = this.querySelector(
      'input[value="purchaseTypeSubscription"]'
    );
    const selectedInitialVariant = this.product.variants.find(
      variant => variant.id === Number(this.initialVariant)
    );
    this.currentVariant = selectedInitialVariant || this.product.variants[0];

    // Pre-select subscription if URL contains subscription=true
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('subscription') === 'true') {
      this.purchaseTypeSubscription.classList.add('is-selected');
      this.purchaseTypeOneTime.classList.remove('is-selected');
      if (this.radioSubscription) this.radioSubscription.checked = true;
      if (this.radioOneTime) this.radioOneTime.checked = false;
    }

    this.updateSellingPlans();
  }
  // Set up event listeners for the purchase type, frequency and variant selectors
  setupEventListeners() {
    const purchaseTypeRadioButtons = this.querySelectorAll(
      'input[name="purchaseType"]'
    );
    // Handle purchase type change
    purchaseTypeRadioButtons.forEach(radio =>
      radio.addEventListener('change', () => {
        this[
          radio.value === 'purchaseTypeSubscription'
            ? 'showSubscriptionBox'
            : 'hideSubscriptionBox'
        ]();
        this.updateProductPrice(this.sellingPlanSelector.value);
      })
    );
    // Handle frequency selector change
    this.sellingPlanSelector.addEventListener('change', e => {
      this.updateSellingPlanInput(e.target.value);
      this.updateProductPrice(e.target.value);
    });
    // Handle variant change from URL
    window.addEventListener('popstate', this.updateVariantID());
    this.observeUrlChanges();
    // Handle browser back/forward navigation
    window.addEventListener('pageshow', event => {
      if (
        event.persisted ||
        performance.getEntriesByType('navigation')[0]?.type === 'back_forward'
      ) {
        this.isSubscriptionOptionSelected()
          ? this.querySelector("label[for='purchaseTypeSubscription']").click()
          : this.querySelector("label[for='purchaseTypeOneTime']").click();
        this.updateSellingPlans();
      }
    });
    document.addEventListener('quantity:updated', e => {
      const raw = e.detail?.quantity;
      const n =
        typeof raw === 'number'
          ? raw
          : Number.parseInt(String(raw ?? '').trim(), 10);
      if (!Number.isFinite(n) || n < 1) return;
      this.currentQuantity = n;
      this.updateProductPrice();
    });

    const productForm = this.getProductForm();
    if (productForm) {
      productForm.addEventListener('submit', event => {
        this.enforceSellingPlanGuard();
        if (window.STAY_DEBUG) {
          const formData = new FormData(productForm);
          console.log('[stayai] ATC submit FormData:', [...formData.entries()]);
          console.log(
            '[stayai] product.selling_plan_groups:',
            this.product?.selling_plan_groups
          );
          if (event?.submitter) {
            console.log('[stayai] submitter:', event.submitter);
          }
        }
      });
    }
  }
  // Get variant ID from the URL and update the variant
  updateVariantID = () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has('variant')) return;
    const variantId = Number(urlParams.get('variant'));
    if (variantId && variantId !== this.currentVariant.id) {
      this.currentVariant = this.product.variants.find(v => v.id === variantId);
      this.updateSellingPlans();
    }
  };
  // Monitor URL changes without overriding history methods
  observeUrlChanges = () => {
    let lastUrl = window.location.href;
    new MutationObserver(() => {
      if (window.location.href !== lastUrl) {
        lastUrl = window.location.href;
        this.updateVariantID();
      }
    }).observe(document, { subtree: true, childList: true });
  };
  // Populate the subscription frequency selector with selling plans for the current variant
  updateSellingPlans() {
    const stayWidget = this.querySelector(`.stayai-subscriptions`);
    if (!stayWidget) return;
    // Filter out bundle-type selling plan groups and map them by ID
    const groupMap = new Map(
      this.sellingPlanGroups
        .filter(g => !g.options[0].name.toLowerCase().includes('bundle'))
        .map(g => [g.id, g])
    );
    const sellingPlans = this.currentVariant.selling_plan_allocations
      .map(spa =>
        groupMap
          .get(spa.selling_plan_group_id)
          ?.selling_plans.find(sp => sp.id === spa.selling_plan_id)
      )
      .filter(Boolean);
    if (sellingPlans.length === 0) {
      this.updateSellingPlanInput('');
      stayWidget.classList.toggle('is-visible', false);
      console.log('No SPs, hiding widget');
      return;
    } else stayWidget.classList.toggle('is-visible', true);
    // Clear existing options and populate dropdown
    const defaultPlan = this.getDefaultSellingPlan(sellingPlans);
    this.defaultSellingPlan = defaultPlan;
    this.sellingPlanSelector.replaceChildren(
      ...sellingPlans.map(plan => {
        const option = document.createElement('option');
        option.value = String(plan.id);
        option.textContent = plan.options?.[0]?.value ?? '';
        option.selected = plan.id === defaultPlan.id;
        return option;
      })
    );
    if (!this.isSubscriptionOptionSelected()) {
      this.addSelectFrequencyOption();
      this.updateSellingPlanInput('');
    } else {
      this.updateSellingPlanInput(defaultPlan.id || '');
    }
    // Show or hide the subscription frequency selector
    this.subscriptionBox.classList.toggle(
      'is-visible',
      this.isSubscriptionOptionSelected()
    );
    if (this.subscriptionBoxContainer)
      this.subscriptionBoxContainer.classList.toggle(
        'subscriptionBoxContainer',
        this.isSubscriptionOptionSelected()
      );
    // Update prices
    this.updateProductPrice(
      this.isSubscriptionOptionSelected()
        ? this.sellingPlanSelector.value
        : defaultPlan.id || ''
    );
  }
  // Normalize a selling plan's delivery interval to total days by parsing option text
  // e.g. "Delivered every 14 days*", "Every 2 weeks", "1 month"
  getSellingPlanTotalDays(plan) {
    const text = plan.options?.[0]?.value || '';
    const match = text.match(/(\d+)?\s*(day|week|month|year)/i);
    if (!match) return null;
    const count = match[1] ? parseInt(match[1], 10) : 1;
    const unit = match[2].toLowerCase();
    if (unit === 'day') return count;
    if (unit === 'week') return count * 7;
    if (unit === 'month') return count * 30;
    if (unit === 'year') return count * 365;
    return null;
  }
  // Return the 30-day plan as the default, falling back to the first plan
  getDefaultSellingPlan(sellingPlans) {
    return (
      sellingPlans.find(plan => this.getSellingPlanTotalDays(plan) === 30) ||
      sellingPlans[0]
    );
  }
  // Add "Select frequency" option to the frequency selector
  addSelectFrequencyOption() {
    if (!this.sellingPlanSelector.querySelector('option[value=""]')) {
      const option = document.createElement('option');
      option.value = '';
      option.textContent = 'Select frequency';
      option.selected = true;
      this.sellingPlanSelector.prepend(option);
    }
  }
  // Show the subscription frequency selector
  showSubscriptionBox() {
    this.subscriptionBox.classList.toggle('stayai-disable', false);
    this.subscriptionBox.classList.toggle('is-visible', true);
    if (this.subscriptionBoxContainer)
      this.subscriptionBoxContainer.classList.toggle(
        'subscriptionBoxContainer',
        true
      );
    this.purchaseTypeSubscription.classList.toggle('is-selected', true);
    this.purchaseTypeOneTime.classList.toggle('is-selected', false);
    this.sellingPlanSelector.querySelector('option[value=""]')?.remove();
    if (this.defaultSellingPlan) {
      this.sellingPlanSelector.value = String(this.defaultSellingPlan.id);
    }
    this.updateSellingPlanInput(this.sellingPlanSelector.value);
  }
  // Hide the subscription frequency selector
  hideSubscriptionBox() {
    this.addSelectFrequencyOption();
    if (this.subscriptionBoxContainer)
      this.subscriptionBoxContainer.classList.toggle('is-visible', false);
    this.subscriptionBox.classList.toggle('is-visible', false);
    this.subscriptionBox.classList.toggle('disable', true);
    this.purchaseTypeSubscription.classList.toggle('is-selected', false);
    this.purchaseTypeOneTime.classList.toggle('is-selected', true);
    this.updateSellingPlanInput('');
  }
  // Check if the subscription option is selected
  isSubscriptionOptionSelected() {
    return this.purchaseTypeSubscription.classList.contains('is-selected');
  }
  //Add selling plan input to form with specified selling plan ID
  updateSellingPlanInput(sellingPlanId) {
    const productForm = this.getProductForm();
    if (!productForm) return;

    // Find/create managed input and only allow plans from app_id 5859381.
    const sellingPlanInput = this.ensureManagedSellingPlanInput(productForm);
    const sanitizedSellingPlanId = this.isAllowedSellingPlanId(sellingPlanId)
      ? String(sellingPlanId || '')
      : '';
    sellingPlanInput.value = sanitizedSellingPlanId;
    this.enforceSellingPlanGuard();
    // Dispatch event with the selling plan value
    this.fireStayAiEvent(
      'stay-ai-subscription-changed',
      sanitizedSellingPlanId
    );
  }
  updateProductPrice(sellingPlanId) {
    if (sellingPlanId !== undefined && sellingPlanId !== null) {
      this._lastSellingPlanIdForPrice = sellingPlanId;
    }
    sellingPlanId = this._lastSellingPlanIdForPrice;
    const fallbackSellingPlanId =
      this.currentVariant?.selling_plan_allocations?.[0]?.selling_plan_id || '';
    const effectiveSellingPlanId = sellingPlanId || fallbackSellingPlanId;

    const qty = Math.max(1, this.currentQuantity || 1);
    const unitPrice = this.currentVariant.price;
    const unitCompareAt = this.currentVariant.compare_at_price || 0;

    const selectedSellingPlanAllocation =
      this.currentVariant.selling_plan_allocations.find(
        spa => spa.selling_plan_id == effectiveSellingPlanId
      );
    const sellingPlan = this.sellingPlanGroups
      .find(group =>
        group.selling_plans.some(sp => sp.id == effectiveSellingPlanId)
      )
      ?.selling_plans.find(sp => sp.id == effectiveSellingPlanId);
    const savingLbl = this.querySelector('.subscription-savings');
    const onetimePrice = this.formatMoney(unitPrice * qty);
    let subscriptionPrice = onetimePrice;

    // Update subscription price / savings label
    if (selectedSellingPlanAllocation) {
      subscriptionPrice = this.formatMoney(
        selectedSellingPlanAllocation.price * qty
      );
      // Calculate and update savings label
      let discount =
        this.currentVariant.price - selectedSellingPlanAllocation.price;
      if (discount > 0) {
        if (sellingPlan?.price_adjustments[0].value_type === 'percentage') {
          discount = `${sellingPlan.price_adjustments[0].value}%`;
        } else {
          discount = this.formatMoney(discount);
        }
        if (savingLbl.textContent !== discount)
          savingLbl.textContent = discount;
      } else {
        savingLbl.textContent = '';
      }
    } else {
      if (savingLbl) savingLbl.textContent = '';
    }
  }
  // Toggle checked state based on the currently checked radio button
  toggleRadioButtons() {
    this.radioOneTime.checked = !this.radioOneTime.checked;
    this.radioSubscription.checked = !this.radioSubscription.checked;
  }
  // Format money based on Shopify's active currency
  formatMoney(money) {
    const { Shopify, theme } = window;
    const currency = Shopify?.currency?.active || 'USD';
    const locale = Shopify?.locale || 'en';
    const hasShopifyFormatMoney =
      Shopify && typeof Shopify?.formatMoney === 'function';
    const hasThemeUtilsFormatMoney =
      theme?.utils && typeof theme?.utils?.formatMoney === 'function';
    const formattedMoney = (money / 100).toFixed(2);
    let formatted;
    const currencyFormatter = new Intl.NumberFormat(locale, {
      currency,
      style: 'currency'
    });
    if (!hasShopifyFormatMoney && !hasThemeUtilsFormatMoney) {
      return currencyFormatter.format(Number(formattedMoney));
    }
    formatted = hasShopifyFormatMoney
      ? Shopify.formatMoney(money)
      : theme.utils.formatMoney(money);
    return formatted || currencyFormatter.format(Number(formattedMoney));
  }
  fireStayAiEvent(eventName, sellingPlanId) {
    const event = new CustomEvent(eventName, {
      detail: {
        sellingPlanId: sellingPlanId
      }
    });
    window.dispatchEvent(event);
  }
}
// Define the custom element
customElements.define('purchase-options', StayAISubscriptions);
